import MatomoTracker from '@datapunt/matomo-tracker-js';
import { CustomDimension } from '@datapunt/matomo-tracker-js/lib/types';
import { useCallback } from 'react';
import { getUserInfo } from "../util/author";
import { readRegistrationAttributionState } from "../util/registrationAttribution";

// 自定义维度映射
const CUSTOM_DIMENSIONS = {
  USER_ID: 1,
  CLICK_TIMESTAMP: 2,
  BOARD_ID: 3,
  CARD_ID: 4,
  MESSAGE_ID: 5,
  SEND_FROM: 6,
  PROMPT_TYPE: 7,
  MODE_TYPE: 8,
  TOOL_TYPE: 9,
  LOAD_TIME: 10,
  BILLING_POPUP_FROM: 11,
  SUBSCRIPTION_PLAN: 12,
  BROWSER_VERSION: 13,
  SEND_VERIFICATION_CODE_STATUS: 14,
  REGISTER_STATUS: 15,
  REGISTER_FROM: 16,
  BOARD_SELECTION_FROM: 17,
  BOARD_TYPE: 18,
  MODEL_NAME: 19,
  IMAGE_ID: 20,
  PAGE_ID: 21,
  REFERRER: 22,
  UTM: 23,
  DEVICE_ID: 24,
  COUNTRY: 25,
  BROWSER: 26,
  LANDING_PAGE: 27,
  SWITCH_FROM: 28,
  CREDITS_ACCOUNT: 29,
  REDEEM_STATUS: 30,
  LOGIN_STATUS: 31,
  SELECT_CONTENT: 32,
  CREATE_CARD_ID: 33,
  TRIGGER_TIMESTAMP: 34,
  IMAGE_DOWNLOAD_FROM: 35,
  IS_GENERATED: 36,
  IMAGE_COPY_FROM: 37,
  INVITE_ENTER_FROM: 38,
  PACKAGE_SHOW_FROM: 39,
  WATERDROP_AMOUNT: 40,
  SHOW_TIME: 41,
  LOGIN_ERROR_KEY: 42,
  VERIFICATION_TYPE: 43,
  PHONE_EMAIL: 44,
  VERIFICATION_ERROR_KEY: 45,
  IMAGE_SIZE: 46,
  IMAGE_QUALITY: 47,
  FORMAT_ID: 48,
  FORMAT_TAG: 49,
  GENERATE_MODE: 50,
  LOGIN_SWITCH_TO: 51,
  SWITCH_STATUS: 52,
  ATTRIBUTION_CHANNEL: 53,
  ATTRIBUTION_SOURCE_ROOT_DOMAIN: 54,
  ATTRIBUTION_SUMMARY: 55,
} as const;

// 公共参数
interface BaseEventParams {
}

interface PromptSendEventParams extends BaseEventParams {
  send_from: string;
  board_id: string;
  card_id: string;
  prompt_type: string;
  message_id: string;
  mode_type: string;
  generate_mode?: string;
}

interface PromptFormatPopupShowEventParams extends BaseEventParams {
  board_id?: string;
  page_id?: string;
  mode_type?: string;
}

interface AiReplyEventParams extends BaseEventParams {
  load_time: number;
  tool_type: string;
  message_id: string;
  board_id: string;
  card_id: string;
}

interface BillingPopupShowEventParams extends BaseEventParams {
  billing_popup_from: string;
  board_id?: string | null;
}

interface SubscriptionPlanSelectEventParams extends BaseEventParams {
  subscription_plan: string;
  switch_from?: string
}

interface SubscriptionPlanPopupConfirmEventParams extends BaseEventParams {
  page_id?: string;
  switch_status: string;
  select_content?: string;
}

/** 续费方式选择：参数 user_id, device_id, click_timestamp, page_id, select_content（立即生效/到期生效） */
interface RenewalPlanChooseEventParams extends BaseEventParams {
  page_id?: string;
  select_content?: string;
}

interface QrcodeShowStatusEventParams extends BaseEventParams {
  subscription_plan: string;
  switch_status?: string;
  switch_from?: string;
}

interface WaterdropDetailClickEventParams extends BaseEventParams {
  page_id?: string;
  subscription_plan?: string;
  waterdrop_amount?: string | number;
}

interface NotificationInboxClickEventParams extends BaseEventParams {
  page_id?: string;
}

interface NotificationGoClickEventParams extends BaseEventParams {
  page_id?: string;
  select_content?: string;
  switch_status?: string
}

interface NotificationClickEventParams extends BaseEventParams {
  page_id?: string;
  select_content?: string;
}

/** 预制聊点击：参数 user_id, device_id, click_timestamp, page_id, select_content */
interface QuickRepliesClickEventParams extends BaseEventParams {
  page_id?: string;
  select_content?: string;
  device_id?: string;
}

interface InvoiceEntryClickEventParams extends BaseEventParams {
  page_id?: string;
  subscription_plan?: string;
}

interface ConsumptionRecordClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
}

interface InvoiceApplyClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  switch_status?: string;
}

interface InvoiceFormSubmitEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
}

/** 关注领水滴表单提交：参数 user_id, device_id, click_timestamp, page_id */
interface FollowActivityCommitEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
}

/** 上传关注截图：参数 user_id, device_id, click_timestamp, page_id, select_content */
interface UploadFollowScreenshotEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  select_content?: string;
}

/** 关注官号点击：参数 user_id, device_id, click_timestamp, page_id, select_content */
interface FollowAccountClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  select_content?: string;
}

/** 客服头像弹窗展示：参数 user_id, device_id, click_timestamp, page_id */
interface SupportPopupShowEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
}

/** 客服头像-联系客服点击 */
interface ContactSupportClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
}

/** 客服头像-官方公告点击 */
interface OfficialNoticeClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
}

/** 客服头像-教程文档点击 */
interface UserGuideClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
}

/** 会员身份点击：参数 user_id, device_id, click_timestamp, page_id */
interface MemberIdentityClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
}

/** 首页提示词曝光：参数 user_id, device_id, click_timestamp, page_id */
interface HomePromptCaseShowEventParams extends BaseEventParams {
  page_id?: string;
}

interface HomeRegisterClickEventParams extends BaseEventParams {
}

interface SendVerificationCodeClickEventParams extends BaseEventParams {
  send_verification_code_status: number;
  verification_type?: string;
  device_id?: string;
  phone_email?: string;
  verification_error_key?: string;
}

interface VisitorChatboxClickEventParams extends BaseEventParams {
}

interface RegisterPopupRegisterClickEventParams extends BaseEventParams {
  register_status: number;
}

interface VisitorBoardRegisterClickEventParams extends BaseEventParams {
}

interface RegisterPopupShowEventParams extends BaseEventParams {
  register_from: string;
  device_id?: string;
}

interface RegisterEntryAbExposureEventParams extends BaseEventParams {
  variant: 'popup' | 'natural';
  device_id?: string;
}

interface BoardCreateClickFromHomeEventParams extends BaseEventParams {
}

interface BoardCreateClickFromBoardManagementEventParams extends BaseEventParams {
}

interface BoardDeleteEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  board_id: string;
}

interface BoardDeleteConfirmEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  board_id: string;
  switch_status: 'confirm' | 'cancel';
}

interface BoardRenameEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  board_id: string;
}

interface BoardRenameConfirmEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  board_id: string;
  switch_status: 'confirm' | 'cancel';
}

interface AlbumCreateEventParams extends BaseEventParams {
  page_id?: string;
}

interface AlbumDeleteEventParams extends BaseEventParams {
  page_id?: string;
  format_id?: string;
}

interface AlbumDeleteConfirmEventParams extends BaseEventParams {
  page_id?: string;
  format_id?: string;
  select_content?: string;
  switch_status?: string;
}

interface BoardMenuClickEventParams extends BaseEventParams {
  page_id?: string;
  board_id?: string;
}

interface MoveBoardClickEventParams extends BaseEventParams {
  page_id?: string;
  board_id?: string;
}

interface MoveBoardConfirmEventParams extends BaseEventParams {
  page_id?: string;
  board_id?: string;
  switch_status?: string;
}

interface SelectBoardClickEventParams extends BaseEventParams {
  page_id?: string;
  board_id?: string;
}

interface InvoiceApplyHoverShowEventParams extends BaseEventParams {
  page_id?: string;
}

interface HomeSidebarFoldEventParams extends BaseEventParams {
  page_id?: string;
  switch_status?: string;
}

interface ShowcaseClickEventParams extends BaseEventParams {
  page_id?: string;
  board_id?: string;
}

interface FolderEnterClickEventParams extends BaseEventParams {
  page_id?: string;
  format_id?: string;
}

interface SidebarHomeClickEventParams extends BaseEventParams {
  page_id?: string;
}

interface BoardSpaceClickEventParams extends BaseEventParams {
  page_id?: string;
  switch_from?: string;
}

interface BoardSelectionPopupShowEventParams extends BaseEventParams {
  board_selection_from: string;
}

interface BoardTypeSelectEventParams extends BaseEventParams {
  board_type: string;
  board_id?: string;
}

interface EnterBoardEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  board_id?: string;
  board_type?: string;
}

interface ImageGenerateFinishEventParams extends BaseEventParams {
  load_time: number;
  board_id: string;
  card_id: string;
  message_id: string;
  tool_type: string;
  model_name: string;
  image_id: string;
}

interface ImageGenerateViewEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  image_id: string;
  tool_type: string;
}

interface PageViewEventParams extends BaseEventParams {
  load_time: number;
  page_id: string;
  referrer: string;
  utm: string;
  login_status?: string;
}

interface VisitorSessionStartEventParams {
  device_id: string;
  country: string;
  browser: string;
  landing_page: string;
  referrer: string;
  utm: string;
  login_status?: string;
}

interface ModeTypeSwitchEventParams extends BaseEventParams {
  switch_from: string;
  board_id: string;
  card_id: string;
  mode_type: string;
}

/** 思考过程展示折叠点击：参数 board_id, card_id, switch_status */
interface DeepthinkingFoldClickEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  switch_status: string;
}

/** 智能参考图开关点击：参数 board_id, card_id, switch_status */
interface SmartReferenceSwitchClickEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  switch_status: string;
}

interface GiftCodeRedeemEventParams extends BaseEventParams {
}

interface GiftCodeRedeemStatusEventParams extends BaseEventParams {
  credits_account: number | null;
  redeem_status: string;
}

interface HomeLoginClickEventParams extends BaseEventParams {
}

interface LoginPopupLoginClickEventParams extends BaseEventParams {
  login_status?: string;
  device_id?: string;
  login_error_key?: string;
}

interface RegisterPopupLoginClickEventParams extends BaseEventParams {
}

interface ShareClickEventParams extends BaseEventParams {
  board_id: string | null;
}

interface CardAddToChatEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
}

interface CardDetailEnterEventParams extends BaseEventParams {
  card_id: string;
  board_id: string;
}

interface CardDeleteEventParams extends BaseEventParams {
  card_id: string;
  board_id: string;
}

/** 灵感卡删除二级弹窗：参数 user_id, device_id, click_timestamp, card_id, board_id, switch_status（confirm/cancel） */
interface CardDeleteConfirmEventParams extends BaseEventParams {
  card_id: string;
  board_id: string;
  switch_status: 'confirm' | 'cancel';
}

interface CardContentEditEventParams extends BaseEventParams {
  card_id: string;
  board_id: string;
}

interface CardImageUploadClickEventParams extends BaseEventParams {
  card_id: string;
  board_id: string;
}

interface CardImageUploadingFinishEventParams extends BaseEventParams {
  load_time: number;
  card_id: string;
  image_id: string;
  board_id: string;
}

interface UserMsgEditEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  message_id: string;
}

interface UserMsgCreateNewCardEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  message_id: string;
  create_card_id: string;
}

interface InsertContentClickEventParams extends BaseEventParams {
  message_id: string;
  select_content: string;
  board_id: string;
  card_id: string;
}

interface InsertContentShowEventParams extends BaseEventParams {
  load_time: number;
  tool_type: string;
  message_id: string;
  board_id: string;
  card_id: string;
}

interface InsertContentConfirmClickEventParams extends BaseEventParams {
  tool_type: string;
  message_id: string;
  board_id: string;
  card_id: string;
}

interface InsertContentCancelClickEventParams extends BaseEventParams {
  tool_type: string;
  message_id: string;
  board_id: string;
  card_id: string;
}

interface InsertContentFinishEventParams extends BaseEventParams {
  load_time: number;
  tool_type: string;
  message_id: string;
  board_id: string;
  card_id: string;
}

interface InspireCreateClickEventParams extends BaseEventParams {
  message_id: string;
  board_id: string;
  card_id: string;
}

interface InspireCreateFinishEventParams extends BaseEventParams {
  create_card_id: string;
  message_id: string;
  board_id: string;
}

interface QuoteClickEventParams extends BaseEventParams {
  message_id: string;
  board_id: string;
}

interface QuoteFinishEventParams extends BaseEventParams {
  load_time: number;
  message_id: string;
  board_id: string;
  card_id: string;
}

interface ShowOptionsChooseEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  message_id: string;
  tool_type: string;
}

interface ShowOptionsConfirmEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  message_id: string;
  tool_type: string;
}

interface SuggestInsertConfirmEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  message_id: string;
  tool_type: string;
}

interface SuggestInsertCancelEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  message_id: string;
  tool_type: string;
}

interface SuggestReplaceClickEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  message_id: string;
  tool_type: string;
}

interface SuggestReplaceCancelEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  message_id: string;
  tool_type: string;
}

interface ImageRegenerateClickEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  message_id: string;
  tool_type: string;
  model_name: string;
}

interface ImageGenerateCollectEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  image_id: string;
  tool_type: string;
}

interface ImageSearchViewEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  image_id: string;
  tool_type: string;
}

interface ImageSearchCollectEventParams extends BaseEventParams {
  board_id: string;
  card_id: string;
  image_id: string;
  tool_type: string;
}

interface SuggestCreateTriggerEventParams extends BaseEventParams {
  trigger_timestamp: number;
  board_id: string;
  card_id: string;
  message_id: string;
  tool_type: string;
  create_card_id: string;
}

/** image_download 与 download_without_watermark 共用参数 */
interface ImageDownloadEventParams extends BaseEventParams {
  device_id?: string;
  board_id: string;
  card_id: string;
  image_id: string;
  image_download_from: string;
  is_generated: number;
}

/** image_copy 与 copy_without_watermark 共用参数 */
interface ImageCopyEventParams extends BaseEventParams {
  device_id?: string;
  board_id: string;
  card_id: string;
  image_id: string;
  image_copy_from: string;
  is_generated: number;
}

/** 无水印下载点击：与 image_download 参数一致 */
interface DownloadWithoutWatermarkEventParams extends BaseEventParams {
  device_id?: string;
  board_id: string;
  card_id: string;
  image_id: string;
  image_download_from: string;
  is_generated: number;
}

/** 无水印复制点击：与 image_copy 参数一致 */
interface CopyWithoutWatermarkEventParams extends BaseEventParams {
  device_id?: string;
  board_id: string;
  card_id: string;
  image_id: string;
  image_copy_from: string;
  is_generated: number;
}

/** AI 管理规则弹窗展示 / 下一步实名认证按钮点击 */
interface AiRulesPageEventParams extends BaseEventParams {
  device_id?: string;
  page_id: string;
}

/** 实名认证填写确认 */
interface IdentificationConfirmEventParams extends BaseEventParams {
  device_id?: string;
  page_id: string;
  register_status: number;
}

/** 生图复制到绘版 */
interface CopyToBoardClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id: string;
  image_id: string;
}

interface InvitePopupClickEventParams extends BaseEventParams {
  invite_enter_from: string;
}

interface InvitationLinkCopyEventParams extends BaseEventParams {
}

interface InvitationCodeCopyEventParams extends BaseEventParams {
}

interface WaterdropPackagePopupShowEventParams extends BaseEventParams {
  package_show_from: string;
  page_id?: string;
}

interface WaterdropPackageChooseEventParams extends BaseEventParams {
  page_id?: string;
  waterdrop_amount: number;
  subscription_plan?: string;
}

interface WaterdropPackagePopupShowTimeEventParams extends BaseEventParams {
  show_time: number;
  page_id?: string;
}

interface BillingPopupShowTimeEventParams extends BaseEventParams {
  show_time: number;
  page_id?: string;
}

interface ImageModelSwitchClickEventParams extends BaseEventParams {
  page_id?: string;
  board_id?: string;
  card_id?: string;
}

interface ImageModelChooseClickEventParams extends BaseEventParams {
  page_id?: string;
  board_id?: string;
  card_id?: string;
  model_name?: string;
}

interface ImageSizeChooseEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  image_size?: string;
}

interface ImageQualityChooseEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  image_quality?: string;
}

interface PromptFormatPopupShowTimeEventParams extends BaseEventParams {
  device_id?: string;
  show_time?: number;
  page_id?: string;
}

interface TryPromptFormatClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  board_id?: string;
  card_id?: string;
  model_name?: string;
  format_id?: string;
}

interface PromptFormatSidebarClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  board_id?: string;
  card_id?: string;
  format_tag?: string;
}

interface WaterdropLackPopupShowEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  board_id?: string;
  card_id?: string;
  switch_from?: string
}

interface GetWaterdropClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  board_id?: string;
  card_id?: string;
  switch_from?: string
}

/** 领取活动奖励 */
interface RewardGetClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  select_content?: string;
  login_status?: boolean;
}

/** 水滴活动弹窗点击 */
interface ActivityPopupClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  switch_from?: string;
}

interface WaterdropLackPopupCloseEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  board_id?: string;
  card_id?: string;
  show_time?: number;
}

interface ImageSizeQualityShowEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
}

interface LoginMethodSwitchEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  login_switch_to: string;
}

interface JoinQqgroupClickEventParams extends BaseEventParams {
  device_id?: string;
  page_id?: string;
  board_id?: string;
}

interface YearlySubscriptionPopupShowEventParams extends BaseEventParams {
  device_id: string;
  page_id?: string;
}

interface VideoTutorialClickEventParams extends BaseEventParams {
  device_id?: string;
  board_id?: string;
  page_id?: string;
  send_from?: string;
}

interface QuestionairePopupShowEventParams extends BaseEventParams {
  user_id?: string;
  device_id?: string;
  click_timestamp?: number;
  page_id?: string;
  board_id?: string;
  switch_from?: string;
}

interface QuestionaireSubmitClickEventParams extends BaseEventParams {
  user_id?: string;
  device_id?: string;
  click_timestamp?: number;
  page_id?: string;
  board_id?: string;
  switch_from?: string;
}

/* ---- Blog 侧边栏埋点 ---- */

interface BilibiliVideosClickEventParams extends BaseEventParams {
  page_id?: string;
  message_id?: string;
}

interface LeftsideEntryExposureEventParams extends BaseEventParams {
  page_id?: string;
}

interface BlogReadClickEventParams extends BaseEventParams {
  page_id?: string;
  message_id?: string;
}

interface BlogSeeMoreClickEventParams extends BaseEventParams {
  page_id?: string;
}

/** 视频轮播切换点击：参数 user_id, device_id, click_timestamp, page_id, switch_from（向右/向左） */
interface VideoSwitchClickEventParams extends BaseEventParams {
  page_id?: string;
  switch_from?: string;
}

/** 顶部 B 站视频点击：参数 user_id, device_id, click_timestamp, page_id, message_id */
interface BilibiliTopVideosClickEventParams extends BaseEventParams {
  page_id?: string;
  message_id?: string;
}

interface ThemeModeSelectEventParams extends BaseEventParams {
  select_content: string;  // 选中的模式：light / dark / system
  switch_from?: string;    // 切换前的模式
  page_id?: string;
}

let matomoTracker: MatomoTracker | null = null;

// 初始化Matomo跟踪器
export const initMatomoTracker = (options: {
  urlBase: string;
  siteId: number;
  trackerUrl?: string;
  srcUrl?: string;
  disabled?: boolean;
  heartBeat?: { active: boolean; seconds: number };
  linkTracking?: boolean;
  userId?: string;
}) => {
  if (typeof window !== 'undefined') {
    matomoTracker = new MatomoTracker(options);
  }
};

export const setMatomoVisitorId = (visitorId: string) => {
  if (!visitorId) return;
  matomoTracker?.pushInstruction('setVisitorId', visitorId);
};

/** 登录后设置 Matomo 原生 userId（uuid），登出时传空/不传会 resetUserId */
export const setMatomoUserId = (uuid?: string | null) => {
  if (!uuid) {
    matomoTracker?.pushInstruction('resetUserId');
  } else {
    matomoTracker?.pushInstruction('setUserId', uuid);
  }
};

const createCustomDimension = (id: number, value: string | number | null | undefined | boolean): CustomDimension | null => {
  if (value == null) return null;
  return {
    id,
    value: String(value)
  };
};

const buildCustomDimensions = (dimensions: Array<CustomDimension | null>): CustomDimension[] => {
  const merged = new Map<number, CustomDimension>();
  for (const dim of buildAutoCustomDimensions()) {
    merged.set(dim.id, dim);
  }
  for (const dim of dimensions) {
    if (dim) merged.set(dim.id, dim);
  }
  return Array.from(merged.values());
};

const buildAutoCustomDimensions = (): CustomDimension[] => {
  const state = readRegistrationAttributionState();
  const channel = state?.channelHistory?.[state.channelHistory.length - 1]?.channel;
  const sourceRootDomain = state?.source?.referrerRootDomain;
  const loginStatus = getUserInfo() ? 'logged_in' : 'anonymous';

  return [
    createCustomDimension(CUSTOM_DIMENSIONS.LOGIN_STATUS, loginStatus),
    createCustomDimension(CUSTOM_DIMENSIONS.ATTRIBUTION_CHANNEL, channel),
    createCustomDimension(CUSTOM_DIMENSIONS.ATTRIBUTION_SOURCE_ROOT_DOMAIN, sourceRootDomain),
    createCustomDimension(CUSTOM_DIMENSIONS.ATTRIBUTION_SUMMARY, buildAttributionSummary(state)),
  ].filter((dim): dim is CustomDimension => dim !== null);
};

const buildAttributionSummary = (
  state: ReturnType<typeof readRegistrationAttributionState>,
): string | undefined => {
  if (!state) return undefined;
  const lastTrail = state.trail?.[state.trail.length - 1];
  return [
    `visitor=${state.visitorId || 'none'}`,
    `landing=${state.landing?.rootDomain || 'none'}${state.landing?.path || ''}`,
    `refHost=${state.source?.referrerHost || 'none'}`,
    `trail=${state.trail?.length || 0}`,
    `last=${lastTrail?.url?.path || 'none'}`,
  ].join(';').slice(0, 255);
};

const getUserId = () => {
  const user = getUserInfo();
  return user?.uuid || user?.id?.toString() || 'unknown';
};

const getDeviceId = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;
  return localStorage.getItem('device_id') ?? undefined;
};

export const useMatomoTrackingPoint = () => {
  const getBrowserUserAgent = () => {
    if (typeof window !== 'undefined' && window.navigator) {
      return window.navigator.userAgent || 'unknown';
    }
    return 'unknown';
  };

  const trackPromptSend = useCallback((params: PromptSendEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.SEND_FROM, params.send_from),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.PROMPT_TYPE, params.prompt_type),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MODE_TYPE, params.mode_type),
      createCustomDimension(CUSTOM_DIMENSIONS.GENERATE_MODE, params.generate_mode),
    ]);

    matomoTracker?.trackEvent({
      category: 'Prompt',
      action: 'prompt_send',
      name: '发送prompt',
      value: 1,
      customDimensions
    });
  }, []);

  const trackPromptFormatPopupShow = useCallback((params: PromptFormatPopupShowEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MODE_TYPE, params.mode_type),
    ]);

    matomoTracker?.trackEvent({
      category: 'Prompt',
      action: 'prompt_format_popup_show',
      name: '提示词模板页面展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackAiReply = useCallback((params: AiReplyEventParams) => {
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.LOAD_TIME, params.load_time),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'AI',
      action: 'ai_reply',
      name: 'AI回复',
      value: 1,
      customDimensions
    });
  }, []);

  const trackBillingPopupShow = useCallback((params: BillingPopupShowEventParams) => {
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.BILLING_POPUP_FROM, params.billing_popup_from),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Billing',
      action: 'billing_popup_show',
      name: '充值页面展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackSubscriptionPlanSelect = useCallback((params: SubscriptionPlanSelectEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.SUBSCRIPTION_PLAN, params.subscription_plan),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_FROM, params.switch_from),
    ]);

    matomoTracker?.trackEvent({
      category: 'Billing',
      action: 'subscription_plan_select',
      name: '订阅计划选择',
      value: 1,
      customDimensions
    });
  }, []);

  const trackSubscriptionPlanPopupConfirm = useCallback((params: SubscriptionPlanPopupConfirmEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const deviceId = getDeviceId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_STATUS, params.switch_status),
      createCustomDimension(CUSTOM_DIMENSIONS.SELECT_CONTENT, params.select_content),
    ]);

    matomoTracker?.trackEvent({
      category: 'Billing',
      action: 'subscription_plan_popuo_confirm',
      name: '订阅计划弹窗确认',
      value: 1,
      customDimensions
    });
  }, []);

  const trackRenewalPlanChoose = useCallback((params: RenewalPlanChooseEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const deviceId = getDeviceId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SELECT_CONTENT, params.select_content),
    ]);

    matomoTracker?.trackEvent({
      category: 'Billing',
      action: 'renewal_plan_choose',
      name: '续费方式选择',
      value: 1,
      customDimensions
    });
  }, []);

  const trackQrcodeShowStatus = useCallback((params: QrcodeShowStatusEventParams) => {
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.SUBSCRIPTION_PLAN, params.subscription_plan),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_STATUS, params.switch_status),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_FROM, params.switch_from),
    ]);

    matomoTracker?.trackEvent({
      category: 'Billing',
      action: 'qrcode_show_status',
      name: '充值二维码展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackWaterdropDetailClick = useCallback((params: WaterdropDetailClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const deviceId = getDeviceId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SUBSCRIPTION_PLAN, params.subscription_plan),
      createCustomDimension(CUSTOM_DIMENSIONS.WATERDROP_AMOUNT, params.waterdrop_amount != null ? String(params.waterdrop_amount) : undefined),
    ]);

    matomoTracker?.trackEvent({
      category: 'Billing',
      action: 'waterdrop_detail_click',
      name: '查看水滴详情',
      value: 1,
      customDimensions
    });
  }, []);

  const trackNotificationInboxClick = useCallback((params: NotificationInboxClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const deviceId = getDeviceId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Notification',
      action: 'notification_inbox_click',
      name: '消息中心点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackNotificationGoClick = useCallback((params: NotificationGoClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const deviceId = getDeviceId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SELECT_CONTENT, params.select_content),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_STATUS, params.switch_status),
    ]);

    matomoTracker?.trackEvent({
      category: 'Notification',
      action: 'notification_go_click',
      name: '站内信立即前往点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackNotificationClick = useCallback((params: NotificationClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const deviceId = getDeviceId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SELECT_CONTENT, params.select_content),
    ]);

    matomoTracker?.trackEvent({
      category: 'Notification',
      action: 'notification_click',
      name: '消息中心消息点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackQuickRepliesClick = useCallback((params: QuickRepliesClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SELECT_CONTENT, params.select_content),
    ]);

    matomoTracker?.trackEvent({
      category: 'Chat',
      action: 'quick_replies_click',
      name: '预制聊点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInvoiceEntryClick = useCallback((params: InvoiceEntryClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const deviceId = getDeviceId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SUBSCRIPTION_PLAN, params.subscription_plan),
    ]);

    matomoTracker?.trackEvent({
      category: 'Billing',
      action: 'invoice_entry_click',
      name: '发票入口点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackConsumptionRecordClick = useCallback((params: ConsumptionRecordClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Billing',
      action: 'consumption_record_click',
      name: '消费记录按钮点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInvoiceApplyClick = useCallback((params: InvoiceApplyClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_STATUS, params.switch_status),
    ]);

    matomoTracker?.trackEvent({
      category: 'Invoice',
      action: 'invoice_apply_click',
      name: '开票申请按钮点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInvoiceFormSubmit = useCallback((params: InvoiceFormSubmitEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Invoice',
      action: 'invoice_form_submit',
      name: '开票表单提交点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackFollowActivityCommit = useCallback((params: FollowActivityCommitEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Activity',
      action: 'follow_activity_commit',
      name: '关注领水滴表单提交',
      value: 1,
      customDimensions
    });
  }, []);

  const trackUploadFollowScreenshot = useCallback((params: UploadFollowScreenshotEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SELECT_CONTENT, params.select_content),
    ]);

    matomoTracker?.trackEvent({
      category: 'Activity',
      action: 'upload_follow_screenshot',
      name: '上传关注截图',
      value: 1,
      customDimensions
    });
  }, []);

  const trackFollowAccountClick = useCallback((params: FollowAccountClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SELECT_CONTENT, params.select_content),
    ]);

    matomoTracker?.trackEvent({
      category: 'Activity',
      action: 'follow_account_click',
      name: '关注官号点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackSupportPopupShow = useCallback((params: SupportPopupShowEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'CustomerService',
      action: 'support_popup_show',
      name: '客服头像弹窗展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackContactSupportClick = useCallback((params: ContactSupportClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'CustomerService',
      action: 'contact_support_click',
      name: '客服头像-联系客服点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackOfficialNoticeClick = useCallback((params: OfficialNoticeClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'CustomerService',
      action: 'official_notice_click',
      name: '客服头像-官方公告点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackUserGuideClick = useCallback((params: UserGuideClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'CustomerService',
      action: 'user_guide_click',
      name: '客服头像-教程文档点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackMemberIdentityClick = useCallback((params: MemberIdentityClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Member',
      action: 'member_identity_click',
      name: '会员身份点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackHomeRegisterClick = useCallback((params: HomeRegisterClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const browserVersion = getBrowserUserAgent();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BROWSER_VERSION, browserVersion),
    ]);

    matomoTracker?.trackEvent({
      category: 'Register',
      action: 'home_register_click',
      name: '首页注册按钮',
      value: 1,
      customDimensions
    });
  }, []);

  const trackSendVerificationCodeClick = useCallback(
      (params: SendVerificationCodeClickEventParams) => {
        const clickTimestamp = Date.now();
        const userId = getUserId();

        const customDimensions = buildCustomDimensions([
          createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
          createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
          createCustomDimension(CUSTOM_DIMENSIONS.SEND_VERIFICATION_CODE_STATUS, params.send_verification_code_status),
          createCustomDimension(CUSTOM_DIMENSIONS.VERIFICATION_TYPE, params.verification_type),
          createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
          createCustomDimension(CUSTOM_DIMENSIONS.PHONE_EMAIL, params.phone_email),
          createCustomDimension(CUSTOM_DIMENSIONS.VERIFICATION_ERROR_KEY, params.verification_error_key),
        ]);

        matomoTracker?.trackEvent({
          category: 'Register',
          action: 'send_verification_code_click',
          name: '发送验证码按钮点击',
          value: 1,
          customDimensions
        });
      },
      []
  );

  const trackVisitorChatBoxClick = useCallback((params: VisitorChatboxClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const browserVersion = getBrowserUserAgent();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BROWSER_VERSION, browserVersion),
    ]);

    matomoTracker?.trackEvent({
      category: 'Visitor',
      action: 'visitor_chatbox_click',
      name: '游客点击首页对话框触发注册弹窗',
      value: 1,
      customDimensions
    });
  }, []);

  const trackRegisterPopupRegisterClick = useCallback(
      (params: RegisterPopupRegisterClickEventParams) => {
        const clickTimestamp = Date.now();
        const userId = getUserId();

        const customDimensions = buildCustomDimensions([
          createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
          createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
          createCustomDimension(CUSTOM_DIMENSIONS.REGISTER_STATUS, params.register_status),
        ]);

        matomoTracker?.trackEvent({
          category: 'Register',
          action: 'register_popup_register_click',
          name: '注册弹窗内部：注册按钮点击',
          value: 1,
          customDimensions
        });
      },
      []
  );

  const trackVisitorBoardRegisterClick = useCallback(
      (params: VisitorBoardRegisterClickEventParams = {}) => {
        const clickTimestamp = Date.now();
        const browserVersion = getBrowserUserAgent();
        const userId = getUserId();

        const customDimensions = buildCustomDimensions([
          createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
          createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
          createCustomDimension(CUSTOM_DIMENSIONS.BROWSER_VERSION, browserVersion),
        ]);

        matomoTracker?.trackEvent({
          category: 'Visitor',
          action: 'visitor_board_register_click',
          name: '游客进入画板内部点击注册按钮',
          value: 1,
          customDimensions
        });
      },
      []
  );

  const trackRegisterPopupShow = useCallback((params: RegisterPopupShowEventParams) => {
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.REGISTER_FROM, params.register_from),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
    ]);

    matomoTracker?.trackEvent({
      category: 'Register',
      action: 'register_popup_show',
      name: '注册弹窗展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackRegisterEntryAbExposure = useCallback((params: RegisterEntryAbExposureEventParams) => {
    const experimentVariant = `register_popup_v1:${params.variant}`;
    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, getUserId()),
      createCustomDimension(CUSTOM_DIMENSIONS.REGISTER_FROM, experimentVariant),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
    ]);

    matomoTracker?.trackEvent({
      category: 'Register',
      action: 'register_entry_ab_exposure',
      name: experimentVariant,
      value: 1,
      customDimensions
    });
  }, []);

  const trackBoardCreateClickFromHome = useCallback(
      (params: BoardCreateClickFromHomeEventParams) => {
        const clickTimestamp = Date.now();
        const userId = getUserId();

        const customDimensions = buildCustomDimensions([
          createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
          createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
        ]);

        matomoTracker?.trackEvent({
          category: 'Board',
          action: 'board_create_click_from_home',
          name: '首页新建项目点击',
          value: 1,
          customDimensions
        });
      },
      []
  );

  const trackBoardCreateClickFromBoardManagement = useCallback(
      (params: BoardCreateClickFromBoardManagementEventParams) => {
        const clickTimestamp = Date.now();
        const userId = getUserId();

        const customDimensions = buildCustomDimensions([
          createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
          createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
        ]);

        matomoTracker?.trackEvent({
          category: 'Board',
          action: 'board_create_click_from_board_management',
          name: '画板管理页创建新绘板点击',
          value: 1,
          customDimensions
        });
      },
      []
  );

  const trackBoardSelectionPopupShow = useCallback(
      (params: BoardSelectionPopupShowEventParams) => {
        const userId = getUserId();

        const customDimensions = buildCustomDimensions([
          createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
          createCustomDimension(CUSTOM_DIMENSIONS.BOARD_SELECTION_FROM, params.board_selection_from),
        ]);

        matomoTracker?.trackEvent({
          category: 'Board',
          action: 'board_selection_popup_show',
          name: '画板选择弹窗展示',
          value: 1,
          customDimensions
        });
      },
      []
  );

  const trackBoardTypeSelect = useCallback((params: BoardTypeSelectEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_TYPE, params.board_type),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Board',
      action: 'board_type_select',
      name: '绘版类型选择',
      value: 1,
      customDimensions
    });
  }, []);

  const trackEnterBoard = useCallback((params: EnterBoardEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_TYPE, params.board_type),
    ]);

    matomoTracker?.trackEvent({
      category: 'Board',
      action: 'enter_board',
      name: '进入绘板',
      value: 1,
      customDimensions
    });
  }, []);

  const trackImageGenerateFinish = useCallback((params: ImageGenerateFinishEventParams) => {
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.LOAD_TIME, params.load_time),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
      createCustomDimension(CUSTOM_DIMENSIONS.MODEL_NAME, params.model_name),
      createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_ID, params.image_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Image',
      action: 'image_generate_finish',
      name: '生图结束并加载完成',
      value: 1,
      customDimensions
    });
  }, []);

  const trackModeTypeSwitch = useCallback((params: ModeTypeSwitchEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_FROM, params.switch_from),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MODE_TYPE, params.mode_type),
    ]);

    matomoTracker?.trackEvent({
      category: 'Mode',
      action: 'mode_type_switch',
      name: '智能生图模式切换',
      value: 1,
      customDimensions
    });
  }, []);

  const trackDeepthinkingFoldClick = useCallback((params: DeepthinkingFoldClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_STATUS, params.switch_status),
    ]);

    matomoTracker?.trackEvent({
      category: 'AI',
      action: 'deepthinking_fold_click',
      name: '思考过程展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackSmartReferenceSwitchClick = useCallback((params: SmartReferenceSwitchClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_STATUS, params.switch_status),
    ]);

    matomoTracker?.trackEvent({
      category: 'AI',
      action: 'smart_reference_switch_click',
      name: '智能参考图开关',
      value: 1,
      customDimensions
    });
  }, []);

  const trackGiftCodeRedeem = useCallback((params: GiftCodeRedeemEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    ]);

    matomoTracker?.trackEvent({
      category: 'Gift',
      action: 'gift_code_redeem',
      name: '兑换礼品码',
      value: 1,
      customDimensions
    });
  }, []);

  const trackGiftCodeRedeemStatus = useCallback((params: GiftCodeRedeemStatusEventParams) => {
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CREDITS_ACCOUNT, params.credits_account),
      createCustomDimension(CUSTOM_DIMENSIONS.REDEEM_STATUS, params.redeem_status),
    ]);

    matomoTracker?.trackEvent({
      category: 'Gift',
      action: 'gift_code_redeem_status',
      name: '兑换码兑换状态',
      value: 1,
      customDimensions
    });
  }, []);

  const trackHomeLoginClick = useCallback((params: HomeLoginClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const browserVersion = getBrowserUserAgent();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BROWSER_VERSION, browserVersion),
    ]);

    matomoTracker?.trackEvent({
      category: 'Login',
      action: 'home_login_click',
      name: '免费开始按钮',
      value: 1,
      customDimensions
    });
  }, []);

  const trackHomePromptCaseShow = useCallback((params: HomePromptCaseShowEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const deviceId = getDeviceId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Home',
      action: 'home_prompt_case_show',
      name: '首页提示词曝光',
      value: 1,
      customDimensions
    });
  }, []);

  const trackLoginPopupLoginClick = useCallback((params: LoginPopupLoginClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.LOGIN_STATUS, params.login_status),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.LOGIN_ERROR_KEY, params.login_error_key),
    ]);

    matomoTracker?.trackEvent({
      category: 'Login',
      action: 'login_popup_login_click',
      name: '登录/注册按钮点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackRegisterPopupLoginClick = useCallback((params: RegisterPopupLoginClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const browserVersion = getBrowserUserAgent();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BROWSER_VERSION, browserVersion),
    ]);

    matomoTracker?.trackEvent({
      category: 'Login',
      action: 'register_popup_login_click',
      name: '注册弹窗登录按钮点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackShareClick = useCallback((params: ShareClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Share',
      action: 'share_click',
      name: '绘板分享点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackCardAddToChat = useCallback((params: CardAddToChatEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Card',
      action: 'card_add_to_chat',
      name: '添加灵感卡到对话',
      value: 1,
      customDimensions
    });
  }, []);

  const trackCardDetailEnter = useCallback((params: CardDetailEnterEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Card',
      action: 'card_detail_enter',
      name: '进入卡片详情',
      value: 1,
      customDimensions
    });
  }, []);

  const trackCardDelete = useCallback((params: CardDeleteEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Card',
      action: 'card_delete',
      name: '灵感卡删除点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackCardDeleteConfirm = useCallback((params: CardDeleteConfirmEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_STATUS, params.switch_status),
    ]);

    matomoTracker?.trackEvent({
      category: 'Card',
      action: 'card_delete_confirm',
      name: '灵感卡删除二级弹窗',
      value: 1,
      customDimensions
    });
  }, []);

  const trackBoardDelete = useCallback((params: BoardDeleteEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Board',
      action: 'board_delete',
      name: '绘板删除',
      value: 1,
      customDimensions
    });
  }, []);

  const trackBoardDeleteConfirm = useCallback((params: BoardDeleteConfirmEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_STATUS, params.switch_status),
    ]);

    matomoTracker?.trackEvent({
      category: 'Board',
      action: 'board_delete_confirm',
      name: '绘板删除二级弹窗',
      value: 1,
      customDimensions
    });
  }, []);

  const trackBoardRename = useCallback((params: BoardRenameEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Board',
      action: 'board_rename',
      name: '绘板重命名',
      value: 1,
      customDimensions
    });
  }, []);

  const trackBoardRenameConfirm = useCallback((params: BoardRenameConfirmEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_STATUS, params.switch_status),
    ]);

    matomoTracker?.trackEvent({
      category: 'Board',
      action: 'board_rename_confirm',
      name: '绘板重命名二级弹窗',
      value: 1,
      customDimensions
    });
    matomoTracker?.pushInstruction('setCustomDimension', CUSTOM_DIMENSIONS.SWITCH_STATUS, false);
  }, []);

  const trackCardContentEdit = useCallback((params: CardContentEditEventParams) => {
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Card',
      action: 'card_content_edit',
      name: '卡片内容编辑',
      value: 1,
      customDimensions
    });
  }, []);

  const trackCardImageUploadClick = useCallback((params: CardImageUploadClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Card',
      action: 'card_image_upload_click',
      name: '灵感区图片上传按钮点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackCardImageUploadingFinish = useCallback((params: CardImageUploadingFinishEventParams) => {
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.LOAD_TIME, params.load_time),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_ID, params.image_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Card',
      action: 'card_image_uploading_finish',
      name: '灵感区图片上传加载完成',
      value: 1,
      customDimensions
    });
  }, []);

  const trackUserMsgEdit = useCallback((params: UserMsgEditEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Message',
      action: 'user_msg_edit',
      name: '把消息添加到聊天框',
      value: 1,
      customDimensions
    });
  }, []);

  const trackUserMsgCreateNewCard = useCallback((params: UserMsgCreateNewCardEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CREATE_CARD_ID, params.create_card_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Message',
      action: 'user_msg_create_new_card',
      name: '把消息创建为新的灵感卡',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInsertContentClick = useCallback((params: InsertContentClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SELECT_CONTENT, params.select_content),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Insert',
      action: 'insert_content_click',
      name: '插入内容点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInsertContentShow = useCallback((params: InsertContentShowEventParams) => {
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.LOAD_TIME, params.load_time),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Insert',
      action: 'insert_content_show',
      name: '插入内容展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInsertContentConfirmClick = useCallback((params: InsertContentConfirmClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Insert',
      action: 'insert_content_confirm_click',
      name: '插入内容确认点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInsertContentCancelClick = useCallback((params: InsertContentCancelClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Insert',
      action: 'insert_content_cancel_click',
      name: '插入内容取消点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInsertContentFinish = useCallback((params: InsertContentFinishEventParams) => {
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.LOAD_TIME, params.load_time),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Insert',
      action: 'insert_content_finish',
      name: '插入内容完成',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInspireCreateClick = useCallback((params: InspireCreateClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Inspire',
      action: 'inspire_create_click',
      name: '灵感创建点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInspireCreateFinish = useCallback((params: InspireCreateFinishEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.CREATE_CARD_ID, params.create_card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Inspire',
      action: 'inspire_create_finish',
      name: '灵感创建完成',
      value: 1,
      customDimensions
    });
  }, []);

  const trackQuoteClick = useCallback((params: QuoteClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Quote',
      action: 'quote_click',
      name: '引用点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackQuoteFinish = useCallback((params: QuoteFinishEventParams) => {
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.LOAD_TIME, params.load_time),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Quote',
      action: 'quote_finish',
      name: '引用完成',
      value: 1,
      customDimensions
    });
  }, []);

  const trackShowOptionsChoose = useCallback((params: ShowOptionsChooseEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
    ]);

    matomoTracker?.trackEvent({
      category: 'Options',
      action: 'show_options_choose',
      name: '显示选项选择',
      value: 1,
      customDimensions
    });
  }, []);

  const trackShowOptionsConfirm = useCallback((params: ShowOptionsConfirmEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
    ]);

    matomoTracker?.trackEvent({
      category: 'Options',
      action: 'show_options_confirm',
      name: '显示选项确认',
      value: 1,
      customDimensions
    });
  }, []);

  const trackSuggestInsertConfirm = useCallback((params: SuggestInsertConfirmEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
    ]);

    matomoTracker?.trackEvent({
      category: 'Suggest',
      action: 'suggest_insert_confirm',
      name: '建议插入确认',
      value: 1,
      customDimensions
    });
  }, []);

  const trackSuggestInsertCancel = useCallback((params: SuggestInsertCancelEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
    ]);

    matomoTracker?.trackEvent({
      category: 'Suggest',
      action: 'suggest_insert_cancel',
      name: '建议插入取消',
      value: 1,
      customDimensions
    });
  }, []);

  const trackSuggestReplaceClick = useCallback((params: SuggestReplaceClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
    ]);

    matomoTracker?.trackEvent({
      category: 'Suggest',
      action: 'suggest_replace_click',
      name: '建议替换点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackSuggestReplaceCancel = useCallback((params: SuggestReplaceCancelEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
    ]);

    matomoTracker?.trackEvent({
      category: 'Suggest',
      action: 'suggest_replace_cancel',
      name: '建议替换取消',
      value: 1,
      customDimensions
    });
  }, []);

  const trackImageRegenerateClick = useCallback((params: ImageRegenerateClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
      createCustomDimension(CUSTOM_DIMENSIONS.MODEL_NAME, params.model_name),
    ]);

    matomoTracker?.trackEvent({
      category: 'Image',
      action: 'image_regenerate_click',
      name: '重新生图',
      value: 1,
      customDimensions
    });
  }, []);

  const trackImageGenerateCollect = useCallback((params: ImageGenerateCollectEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_ID, params.image_id),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
    ]);

    matomoTracker?.trackEvent({
      category: 'Image',
      action: 'image_generate_collect',
      name: '采集生图结果',
      value: 1,
      customDimensions
    });
  }, []);

  const trackImageSearchView = useCallback((params: ImageSearchViewEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_ID, params.image_id),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
    ]);

    matomoTracker?.trackEvent({
      category: 'Image',
      action: 'image_search_view',
      name: '放大查看搜图结果',
      value: 1,
      customDimensions
    });
  }, []);

  const trackImageSearchCollect = useCallback((params: ImageSearchCollectEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_ID, params.image_id),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
    ]);

    matomoTracker?.trackEvent({
      category: 'Image',
      action: 'image_search__collect',
      name: '采集搜图结果',
      value: 1,
      customDimensions
    });
  }, []);

  const trackSuggestCreateTrigger = useCallback((params: SuggestCreateTriggerEventParams) => {
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.TRIGGER_TIMESTAMP, params.trigger_timestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
      createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
      createCustomDimension(CUSTOM_DIMENSIONS.CREATE_CARD_ID, params.create_card_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Suggest',
      action: 'suggest_create_trigger',
      name: '触发灵感发散组件',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInvitePopupClick = useCallback((params: InvitePopupClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.INVITE_ENTER_FROM, params.invite_enter_from),
    ]);

    matomoTracker?.trackEvent({
      category: 'Invite',
      action: 'invite_popup_click',
      name: '邀请弹窗点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInvitationLinkCopy = useCallback((params: InvitationLinkCopyEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    ]);

    matomoTracker?.trackEvent({
      category: 'Invite',
      action: 'invitation_link_copy',
      name: '邀请弹窗复制邀请链接',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInvitationCodeCopy = useCallback((params: InvitationCodeCopyEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    ]);

    matomoTracker?.trackEvent({
      category: 'Invite',
      action: 'invitation_code_copy',
      name: '邀请弹窗复制邀请码',
      value: 1,
      customDimensions
    });
  }, []);

  const trackWaterdropPackagePopupShow = useCallback((params: WaterdropPackagePopupShowEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PACKAGE_SHOW_FROM, params.package_show_from),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Waterdrop',
      action: 'waterdrop_package_popup_show',
      name: '水滴包付费弹窗展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackWaterdropPackageChoose = useCallback((params: WaterdropPackageChooseEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.WATERDROP_AMOUNT, params.waterdrop_amount),
      createCustomDimension(CUSTOM_DIMENSIONS.SUBSCRIPTION_PLAN, params.subscription_plan),
    ]);

    matomoTracker?.trackEvent({
      category: 'Waterdrop',
      action: 'waterdrop_package_choose',
      name: '水滴包额度点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackWaterdropPackagePopupShowTime = useCallback((params: WaterdropPackagePopupShowTimeEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.SHOW_TIME, params.show_time),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Waterdrop',
      action: 'waterdrop_package_popup_show_time',
      name: '水滴包付费弹窗展示时长',
      value: 1,
      customDimensions
    });
  }, []);

  const trackBillingPopupShowTime = useCallback((params: BillingPopupShowTimeEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.SHOW_TIME, params.show_time),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Billing',
      action: 'billing_popup_show_time',
      name: '充值页面展示时长',
      value: 1,
      customDimensions
    });
  }, []);

  const trackImageModelSwitchClick = useCallback((params: ImageModelSwitchClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Image',
      action: 'image_model_switch_click',
      name: '生图模型切换按钮点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackImageModelChooseClick = useCallback((params: ImageModelChooseClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MODEL_NAME, params.model_name),
    ]);

    matomoTracker?.trackEvent({
      category: 'Image',
      action: 'image_model_choose_click',
      name: '生图模型选择点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackImageSizeChoose = useCallback((params: ImageSizeChooseEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_SIZE, params.image_size),
    ]);

    matomoTracker?.trackEvent({
      category: 'Image',
      action: 'image_size_choose',
      name: '画幅选择',
      value: 1,
      customDimensions
    });
  }, []);

  const trackImageQualityChoose = useCallback((params: ImageQualityChooseEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_QUALITY, params.image_quality),
    ]);

    matomoTracker?.trackEvent({
      category: 'Image',
      action: 'image_quality_choose',
      name: '清晰度选择',
      value: 1,
      customDimensions
    });
  }, []);

  const trackPromptFormatPopupShowTime = useCallback((params: PromptFormatPopupShowTimeEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.SHOW_TIME, params.show_time),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Prompt',
      action: 'prompt_format_popup_show_time',
      name: '提示词模板页面展示时长',
      value: 1,
      customDimensions
    });
  }, []);

  const trackTryPromptFormatClick = useCallback((params: TryPromptFormatClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.MODEL_NAME, params.model_name),
      createCustomDimension(CUSTOM_DIMENSIONS.FORMAT_ID, params.format_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Prompt',
      action: 'try_prompt_format_click',
      name: '提示词模板立即尝试点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackPromptFormatSidebarClick = useCallback((params: PromptFormatSidebarClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.FORMAT_TAG, params.format_tag),
    ]);

    matomoTracker?.trackEvent({
      category: 'Prompt',
      action: 'prompt_format_sidebar_click',
      name: '提示词模板侧边栏点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackWaterdropLackPopupShow = useCallback((params: WaterdropLackPopupShowEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_FROM, params.switch_from)
    ]);

    matomoTracker?.trackEvent({
      category: 'Waterdrop',
      action: 'waterdrop_lack_popup_show',
      name: '水滴不足弹窗展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackPaymentRulesShow = useCallback((params: { page_id?: string }) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const deviceId = getDeviceId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Billing',
      action: 'payment_rules_show',
      name: '水滴付费规则展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackGetWaterdropClick = useCallback((params: GetWaterdropClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_FROM, params.switch_from)
    ]);

    matomoTracker?.trackEvent({
      category: 'Waterdrop',
      action: 'get_waterdrop_click',
      name: '获取水滴按钮点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackRewardGetClick = useCallback((params: RewardGetClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SELECT_CONTENT, params.select_content),
      createCustomDimension(CUSTOM_DIMENSIONS.LOGIN_STATUS, params.login_status),
    ]);

    matomoTracker?.trackEvent({
      category: 'Waterdrop',
      action: 'reward_get_click',
      name: '领取活动奖励',
      value: 1,
      customDimensions
    });
  }, []);

  const trackActivityPopupClick = useCallback((params: ActivityPopupClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_FROM, params.switch_from)
    ]);

    matomoTracker?.trackEvent({
      category: 'Waterdrop',
      action: 'activity_popup_click',
      name: '水滴活动入口点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackWaterdropLackPopupClose = useCallback((params: WaterdropLackPopupCloseEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SHOW_TIME, params.show_time),
    ]);

    matomoTracker?.trackEvent({
      category: 'Waterdrop',
      action: 'waterdrop_lack_popup_close',
      name: '水滴不足弹窗关闭',
      value: 1,
      customDimensions
    });
  }, []);

  const trackImageSizeQualityShow = useCallback((params: ImageSizeQualityShowEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Image',
      action: 'image_size_quality_show',
      name: '画幅选择弹窗展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackLoginMethodSwitch = useCallback((params: LoginMethodSwitchEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.LOGIN_SWITCH_TO, params.login_switch_to),
    ]);

    matomoTracker?.trackEvent({
      category: 'Login',
      action: 'login_method_switch',
      name: '切换登录方式',
      value: 1,
      customDimensions
    });
  }, []);

  const trackJoinQqgroupClick = useCallback((params: JoinQqgroupClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Waterdrop',
      action: 'join_qqgroup_click',
      name: '新人加群领水滴按钮',
      value: 1,
      customDimensions
    });
  }, []);

  const trackAiRulesPopupShow = (params: AiRulesPageEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);
    matomoTracker?.trackEvent({
      category: 'AI',
      action: 'AI_rules_popup_show',
      name: 'AI 管理规则弹窗展示',
      value: 1,
      customDimensions
    });
  };

  const trackNextIdentificationClick = (params: AiRulesPageEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);
    matomoTracker?.trackEvent({
      category: 'AI',
      action: 'next_identification_click',
      name: '下一步实名认证按钮点击',
      value: 1,
      customDimensions
    });
  };

  const trackIdentificationConfirmClick = (params: IdentificationConfirmEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.REGISTER_STATUS, params.register_status),
    ]);
    matomoTracker?.trackEvent({
      category: 'AI',
      action: 'identification_confirm_click',
      name: '实名认证填写确认',
      value: 1,
      customDimensions
    });
  };

  const trackYearlySubscriptionPopupShow = useCallback((params: YearlySubscriptionPopupShowEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Billing',
      action: 'yearly_subscription_popup_show',
      name: '年卡充值页面展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackVideoTutorialClick = useCallback((params: VideoTutorialClickEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SEND_FROM, params.send_from),
    ]);

    matomoTracker?.trackEvent({
      category: 'Tutorial',
      action: 'video_tutorial_click',
      name: '视频教程按钮点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackQuestionairePopupShow = useCallback((params: QuestionairePopupShowEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const deviceId = getDeviceId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_FROM, params.switch_from),
    ]);

    matomoTracker?.trackEvent({
      category: 'Questionaire',
      action: 'questionaire_popup_show',
      name: '问卷弹窗展示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackQuestionaireSubmitClick = useCallback((params: QuestionaireSubmitClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = params.user_id || getUserId();
    const deviceId = getDeviceId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_FROM, params.switch_from),
    ]);

    matomoTracker?.trackEvent({
      category: 'Questionaire',
      action: 'questionaire_submit_click',
      name: '问卷提交点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackInvoiceApplyHoverShow = useCallback((params: InvoiceApplyHoverShowEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);
    matomoTracker?.trackEvent({
      category: 'Invoice',
      action: 'invoice_apply_hover_show',
      name: '申请开票时间显示',
      value: 1,
      customDimensions
    });
  }, []);

  const trackHomeSidebarFold = useCallback((params: HomeSidebarFoldEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_STATUS, params.switch_status),
    ]);
    matomoTracker?.trackEvent({
      category: 'Home',
      action: 'home_sidebar_fold',
      name: '首页侧边栏收起',
      value: 1,
      customDimensions
    });
  }, []);

  const trackShowcaseClick = useCallback((params: ShowcaseClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);
    matomoTracker?.trackEvent({
      category: 'Home',
      action: 'showcase_click',
      name: '首页showcase点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackFolderEnterClick = useCallback((params: FolderEnterClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.FORMAT_ID, params.format_id),
    ]);
    matomoTracker?.trackEvent({
      category: 'Album',
      action: 'folder_enter_click',
      name: '进入画册',
      value: 1,
      customDimensions
    });
  }, []);

  const trackSidebarHomeClick = useCallback((params: SidebarHomeClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);
    matomoTracker?.trackEvent({
      category: 'Navigation',
      action: 'sidebar_home_click',
      name: '侧边栏首页按钮点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackBoardSpaceClick = useCallback((params: BoardSpaceClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();
    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_FROM, params.switch_from),
    ]);
    matomoTracker?.trackEvent({
      category: 'Navigation',
      action: 'board_space_click',
      name: '进入绘板空间',
      value: 1,
      customDimensions
    });
  }, []);

  const trackAlbumCreate = useCallback((params: AlbumCreateEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Album',
      action: 'create_folder_click',
      name: '新建画册点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackAlbumDelete = useCallback((params: AlbumDeleteEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.FORMAT_ID, params.format_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Album',
      action: 'delete_folder_click',
      name: '删除画册',
      value: 1,
      customDimensions
    });
  }, []);

  const trackAlbumDeleteConfirm = useCallback((params: AlbumDeleteConfirmEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.FORMAT_ID, params.format_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SELECT_CONTENT, params.select_content),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_STATUS, params.switch_status),
    ]);

    matomoTracker?.trackEvent({
      category: 'Album',
      action: 'delete_folder_popup_confirm',
      name: '删除画册二级弹窗',
      value: 1,
      customDimensions
    });
  }, []);

  const trackBoardMenuClick = useCallback((params: BoardMenuClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Album',
      action: 'board_menu_click',
      name: '绘版菜单栏',
      value: 1,
      customDimensions
    });
  }, []);

  const trackMoveBoardClick = useCallback((params: MoveBoardClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Album',
      action: 'move_board_click',
      name: '移动绘板点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackMoveBoardConfirm = useCallback((params: MoveBoardConfirmEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_STATUS, params.switch_status),
    ]);

    matomoTracker?.trackEvent({
      category: 'Album',
      action: 'move_board_confirm',
      name: '移动绘板确认',
      value: 1,
      customDimensions
    });
  }, []);

  const trackSelectBoardClick = useCallback((params: SelectBoardClickEventParams = {}) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, getDeviceId()),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    ]);

    matomoTracker?.trackEvent({
      category: 'Album',
      action: 'select_board_click',
      name: '选择绘板点击',
      value: 1,
      customDimensions
    });
  }, []);

  const trackThemeModeSelect = useCallback((params: ThemeModeSelectEventParams) => {
    const clickTimestamp = Date.now();
    const userId = getUserId();

    const customDimensions = buildCustomDimensions([
      createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
      createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
      createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
      createCustomDimension(CUSTOM_DIMENSIONS.SELECT_CONTENT, params.select_content),
      createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_FROM, params.switch_from),
    ]);

    matomoTracker?.trackEvent({
      category: 'Theme',
      action: 'theme_mode_select',
      name: '暗夜模式选择',
      value: 1,
      customDimensions
    });
  }, []);

  return {
    trackPromptSend,
    trackPromptFormatPopupShow,
    trackAiReply,
    trackBillingPopupShow,
    trackSubscriptionPlanSelect,
    trackSubscriptionPlanPopupConfirm,
    trackRenewalPlanChoose,
    trackQrcodeShowStatus,
    trackWaterdropDetailClick,
    trackNotificationInboxClick,
    trackNotificationGoClick,
    trackNotificationClick,
    trackInvoiceEntryClick,
    trackConsumptionRecordClick,
    trackInvoiceApplyClick,
    trackInvoiceFormSubmit,
    trackFollowActivityCommit,
    trackUploadFollowScreenshot,
    trackFollowAccountClick,
    trackSupportPopupShow,
    trackContactSupportClick,
    trackOfficialNoticeClick,
    trackUserGuideClick,
    trackMemberIdentityClick,
    trackHomeRegisterClick,
    trackSendVerificationCodeClick,
    trackVisitorChatBoxClick,
    trackRegisterPopupRegisterClick,
    trackVisitorBoardRegisterClick,
    trackRegisterPopupShow,
    trackRegisterEntryAbExposure,
    trackBoardCreateClickFromHome,
    trackBoardCreateClickFromBoardManagement,
    trackBoardDelete,
    trackBoardDeleteConfirm,
    trackBoardRename,
    trackBoardRenameConfirm,
    trackBoardSelectionPopupShow,
    trackBoardTypeSelect,
    trackEnterBoard,
    trackImageGenerateFinish,
    trackImageGenerateView,
    trackModeTypeSwitch,
    trackGiftCodeRedeem,
    trackGiftCodeRedeemStatus,
    trackHomeLoginClick,
    trackHomePromptCaseShow,
    trackLoginPopupLoginClick,
    trackRegisterPopupLoginClick,
    trackShareClick,
    trackCardAddToChat,
    trackCardDetailEnter,
    trackCardDelete,
    trackCardDeleteConfirm,
    trackCardContentEdit,
    trackCardImageUploadClick,
    trackCardImageUploadingFinish,
    trackUserMsgEdit,
    trackUserMsgCreateNewCard,
    trackInsertContentClick,
    trackInsertContentShow,
    trackInsertContentConfirmClick,
    trackInsertContentCancelClick,
    trackInsertContentFinish,
    trackInspireCreateClick,
    trackInspireCreateFinish,
    trackQuoteClick,
    trackQuoteFinish,
    trackShowOptionsChoose,
    trackShowOptionsConfirm,
    trackSuggestInsertConfirm,
    trackSuggestInsertCancel,
    trackSuggestReplaceClick,
    trackSuggestReplaceCancel,
    trackImageRegenerateClick,
    trackImageGenerateCollect,
    trackImageSearchView,
    trackImageSearchCollect,
    trackSuggestCreateTrigger,
    trackInvitePopupClick,
    trackInvitationLinkCopy,
    trackInvitationCodeCopy,
    trackWaterdropPackagePopupShow,
    trackWaterdropPackageChoose,
    trackWaterdropPackagePopupShowTime,
    trackBillingPopupShowTime,
    trackImageModelSwitchClick,
    trackImageModelChooseClick,
    trackImageSizeChoose,
    trackImageQualityChoose,
    trackPromptFormatPopupShowTime,
    trackTryPromptFormatClick,
    trackPromptFormatSidebarClick,
    trackWaterdropLackPopupShow,
    trackPaymentRulesShow,
    trackGetWaterdropClick,
    trackWaterdropLackPopupClose,
    trackImageSizeQualityShow,
    trackLoginMethodSwitch,
    trackJoinQqgroupClick,
    trackYearlySubscriptionPopupShow,
    trackVideoTutorialClick,
    trackQuestionairePopupShow,
    trackQuestionaireSubmitClick,
    trackQuickRepliesClick,
    trackCopyWithoutWatermark,
    trackDownloadWithoutWatermark,
    trackAiRulesPopupShow,
    trackNextIdentificationClick,
    trackIdentificationConfirmClick,
    trackCopyToBoardClick,
    trackDeepthinkingFoldClick,
    trackSmartReferenceSwitchClick,
    trackRewardGetClick,
    trackInvoiceApplyHoverShow,
    trackHomeSidebarFold,
    trackShowcaseClick,
    trackFolderEnterClick,
    trackSidebarHomeClick,
    trackBoardSpaceClick,
    trackAlbumCreate,
    trackAlbumDelete,
    trackAlbumDeleteConfirm,
    trackBoardMenuClick,
    trackMoveBoardClick,
    trackMoveBoardConfirm,
    trackSelectBoardClick,
    trackActivityPopupClick,
    trackThemeModeSelect
  };
};

// 游客首次埋点移到hooks外部
export const trackVisitorSessionStart = (params: VisitorSessionStartEventParams) => {
  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
    createCustomDimension(CUSTOM_DIMENSIONS.COUNTRY, params.country),
    createCustomDimension(CUSTOM_DIMENSIONS.BROWSER, params.browser),
    createCustomDimension(CUSTOM_DIMENSIONS.LANDING_PAGE, params.landing_page),
    createCustomDimension(CUSTOM_DIMENSIONS.REFERRER, params.referrer),
    createCustomDimension(CUSTOM_DIMENSIONS.UTM, params.utm),
    createCustomDimension(CUSTOM_DIMENSIONS.LOGIN_STATUS, params.login_status),
  ]);

  matomoTracker?.trackEvent({
    category: 'Visitor',
    action: 'visitor_session_start',
    name: '游客首次访问',
    value: 1,
    customDimensions
  });
}

export const trackPageView = (params: PageViewEventParams) => {
  const userId = getUserId();

  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.LOAD_TIME, params.load_time),
    createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    createCustomDimension(CUSTOM_DIMENSIONS.REFERRER, params.referrer),
    createCustomDimension(CUSTOM_DIMENSIONS.UTM, params.utm),
    createCustomDimension(CUSTOM_DIMENSIONS.LOGIN_STATUS, params.login_status),
  ]);

  matomoTracker?.trackEvent({
    category: 'Page',
    action: 'page_view',
    name: '所有页面访问',
    value: 1,
    customDimensions
  });
}

export const trackCopyWithoutWatermark = (params: CopyWithoutWatermarkEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();
  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
    createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_ID, params.image_id),
    createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_COPY_FROM, params.image_copy_from),
    createCustomDimension(CUSTOM_DIMENSIONS.IS_GENERATED, params.is_generated),
  ]);
  matomoTracker?.trackEvent({
    category: 'Image',
    action: 'copy_without_watermark',
    name: '无水印复制图片点击',
    value: 1,
    customDimensions
  });
};

export const trackDownloadWithoutWatermark = (params: DownloadWithoutWatermarkEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();
  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
    createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_ID, params.image_id),
    createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_DOWNLOAD_FROM, params.image_download_from),
    createCustomDimension(CUSTOM_DIMENSIONS.IS_GENERATED, params.is_generated),
  ]);
  matomoTracker?.trackEvent({
    category: 'Image',
    action: 'download_without_watermark',
    name: '无水印下载图片点击',
    value: 1,
    customDimensions
  });
};

export const trackImageCopy = (params: ImageCopyEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();

  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
    createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_ID, params.image_id),
    createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_COPY_FROM, params.image_copy_from),
    createCustomDimension(CUSTOM_DIMENSIONS.IS_GENERATED, params.is_generated),
  ]);

  matomoTracker?.trackEvent({
    category: 'Image',
    action: 'image_copy',
    name: '有水印复制图片点击',
    value: 1,
    customDimensions
  });
}

export const trackImageDownload = (params: ImageDownloadEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();

  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
    createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_ID, params.image_id),
    createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_DOWNLOAD_FROM, params.image_download_from),
    createCustomDimension(CUSTOM_DIMENSIONS.IS_GENERATED, params.is_generated),
  ]);

  matomoTracker?.trackEvent({
    category: 'Image',
    action: 'image_download',
    name: '有水印下载图片点击',
    value: 1,
    customDimensions
  });
}

export const trackImageGenerateView = (params: ImageGenerateViewEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();

  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.BOARD_ID, params.board_id),
    createCustomDimension(CUSTOM_DIMENSIONS.CARD_ID, params.card_id),
    createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_ID, params.image_id),
    createCustomDimension(CUSTOM_DIMENSIONS.TOOL_TYPE, params.tool_type),
  ]);

  matomoTracker?.trackEvent({
    category: 'Image',
    action: 'image_generate_view',
    name: '放大查看生图结果',
    value: 1,
    customDimensions
  });
}

export const trackCopyToBoardClick = (params: CopyToBoardClickEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();
  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    createCustomDimension(CUSTOM_DIMENSIONS.IMAGE_ID, params.image_id),
  ]);
  matomoTracker?.trackEvent({
    category: 'Image',
    action: 'copy_to_board_click',
    name: '图片复制到绘版点击',
    value: 1,
    customDimensions
  });
};

export const trackQuickRepliesClick = (params: QuickRepliesClickEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();
  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, params.device_id ?? getDeviceId()),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    createCustomDimension(CUSTOM_DIMENSIONS.SELECT_CONTENT, params.select_content),
  ]);
  matomoTracker?.trackEvent({
    category: 'Chat',
    action: 'quick_replies_click',
    name: '预制聊点击',
    value: 1,
    customDimensions
  });
};

/* ---- Blog 侧边栏埋点 ---- */

/** 1. 视频点击 */
export const trackBilibiliVideosClick = (params: BilibiliVideosClickEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();
  const deviceId = getDeviceId();
  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
  ]);
  matomoTracker?.trackEvent({
    category: 'Blog',
    action: 'bilibili_videos_click',
    name: '轮播位视频点击',
    value: 1,
    customDimensions
  });
};

/** 2. 左侧入口曝光 */
export const trackLeftsideEntryExposure = (params: LeftsideEntryExposureEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();
  const deviceId = getDeviceId();
  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
  ]);
  matomoTracker?.trackEvent({
    category: 'Blog',
    action: 'leftside_entry_exposure',
    name: '首页提示词曝光',
    value: 1,
    customDimensions
  });
};

/** 3. Blog 点击阅读 */
export const trackBlogReadClick = (params: BlogReadClickEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();
  const deviceId = getDeviceId();
  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
  ]);
  matomoTracker?.trackEvent({
    category: 'Blog',
    action: 'blog_read_click',
    name: '首页提示词图片点击',
    value: 1,
    customDimensions
  });
};

/** 4. Blog 查看更多 */
export const trackBlogSeeMoreClick = (params: BlogSeeMoreClickEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();
  const deviceId = getDeviceId();
  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
  ]);
  matomoTracker?.trackEvent({
    category: 'Blog',
    action: 'blog_see_more_click',
    name: 'Blog查看更多',
    value: 1,
    customDimensions
  });
};

/** 5. 顶部 B 站视频点击 */
export const trackBilibiliTopVideosClick = (params: BilibiliTopVideosClickEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();
  const deviceId = getDeviceId();
  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    createCustomDimension(CUSTOM_DIMENSIONS.MESSAGE_ID, params.message_id),
  ]);
  matomoTracker?.trackEvent({
    category: 'Blog',
    action: 'bilibili_top_videos_click',
    name: '顶部推荐位点击',
    value: 1,
    customDimensions
  });
};

/** 6. 视频轮播切换点击 */
export const trackVideoSwitchClick = (params: VideoSwitchClickEventParams) => {
  const clickTimestamp = Date.now();
  const userId = getUserId();
  const deviceId = getDeviceId();
  const customDimensions = buildCustomDimensions([
    createCustomDimension(CUSTOM_DIMENSIONS.USER_ID, userId),
    createCustomDimension(CUSTOM_DIMENSIONS.DEVICE_ID, deviceId),
    createCustomDimension(CUSTOM_DIMENSIONS.CLICK_TIMESTAMP, clickTimestamp),
    createCustomDimension(CUSTOM_DIMENSIONS.PAGE_ID, params.page_id),
    createCustomDimension(CUSTOM_DIMENSIONS.SWITCH_FROM, params.switch_from),
  ]);
  matomoTracker?.trackEvent({
    category: 'Blog',
    action: 'video_switch_click',
    name: '视频轮播切换点击',
    value: 1,
    customDimensions
  });
};
